// client/src/pages/Recovery/Recovery.js
// Tillfrisknande-vyn. Visar timeline med användarens symptom-rapporter
// över 21 dagar och låter rapportera nya datapunkter via popup.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_CONFIG from '../../config/apiConfig';
import { ALL_SYMPTOMS } from '../../components/shared/constants/symptoms';
import RecoveryTimelineSlider from './components/RecoveryTimelineSlider';
import './Recovery.css';

// SymptomPopup unchanged...
const SymptomPopup = ({ isOpen, onConfirm, onCancel, existingPoints = [] }) => {
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  
  const getDefaultDate = () => {
    if (existingPoints.length === 0) return new Date().toISOString().split('T')[0];
    
    const latestDate = existingPoints.reduce((latest, point) => {
      const pointDate = new Date(point.date);
      return pointDate > latest ? pointDate : latest;
    }, new Date(existingPoints[0].date));
    
    const nextDate = new Date(latestDate);
    nextDate.setDate(nextDate.getDate() + 3);
    return nextDate.toISOString().split('T')[0];
  };
  
  const [selectedDate, setSelectedDate] = useState(getDefaultDate());
  
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(getDefaultDate());
    }
  }, [isOpen, existingPoints]);
  
  // ✅ Använder centrala symptomlistan från shared/constants/symptoms

  const handleSubmit = () => {
    if (selectedSymptoms.length === 0) return;
    
    const newPoint = {
      id: `nya_symptom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'nya_symptom',
      date: selectedDate,
      symptoms: selectedSymptoms,
      source: 'user_recovery'
    };
    
    console.log('🟡 SymptomPopup creating point:', newPoint);
    onConfirm(newPoint);
    setSelectedSymptoms([]);
  };

  if (!isOpen) return null;

  return (
    <div className="symptom-popup-overlay">
      <div className="symptom-popup">
        <div className="popup-header">
          <h3>🟡 Lägg till nya symptom</h3>
        </div>
        
        <div className="popup-content">
          <div className="form-group">
            <label>Datum:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          
          <div className="form-group">
            <label>Symptom som tillkom:</label>
            <div className="symptoms-grid">
              {ALL_SYMPTOMS.map(symptom => (
                <button
                  key={symptom}
                  type="button"
                  className={`symptom-btn ${selectedSymptoms.includes(symptom) ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedSymptoms(prev => 
                      prev.includes(symptom) 
                        ? prev.filter(s => s !== symptom)
                        : [...prev, symptom]
                    );
                  }}
                >
                  {symptom}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="popup-actions">
          <button className="btn-cancel" onClick={onCancel}>
            Avbryt
          </button>
          <button 
            className="btn-confirm" 
            onClick={handleSubmit}
            disabled={selectedSymptoms.length === 0}
          >
            Lägg till
          </button>
        </div>
      </div>
    </div>
  );
};

const Recovery = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [recoveryNotes, setRecoveryNotes] = useState('');
  const [step, setStep] = useState('loading');
  const [timelinePoints, setTimelinePoints] = useState([]);
  const [showSymptomPopup, setShowSymptomPopup] = useState(false);

  useEffect(() => {
    loadRecoveryData();
  }, [code]);

  useEffect(() => {
    if (reportData && step === 'form') {
      initializeTimeline();
    }
  }, [reportData, step]);

  const loadRecoveryData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔍 Loading recovery data for code: ${code}`);
      
      const response = await fetch(`${API_CONFIG.baseUrl}/recovery/${code}`);
      const result = await response.json();
      
      console.log('📊 Recovery API response:', result);
      
      if (result.success) {
        if (result.recovered) {
          setStep('already_recovered');
          setReportData(result.originalReport);
        } else {
          setStep('form');
          setReportData(result.report);
          console.log('✅ Recovery data loaded:', result.report);
          console.log('📅 Report date from API:', result.report?.reportDate);
        }
      } else {
        setError(result.error || 'Recovery code not found');
        setStep('error');
      }
      
    } catch (err) {
      console.error('❌ Error loading recovery data:', err);
      setError('Failed to load recovery data. Please try again.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const initializeTimeline = () => {
    if (!reportData) return;
    
    console.log('📊 reportData:', reportData);
    console.log('📊 reportData.reportDate:', reportData.reportDate);
    
    let startDate;
    if (reportData.reportDate) {
      const parsedDate = new Date(reportData.reportDate);
      if (!isNaN(parsedDate.getTime())) {
        startDate = parsedDate.toISOString().split('T')[0];
      } else {
        // TEST MODE: Ogiltigt datum, simulera 5 dagar sedan
        const testDate = new Date();
        testDate.setDate(testDate.getDate() - 5);
        startDate = testDate.toISOString().split('T')[0];
        console.log('⚠️ TEST MODE: Ogiltigt reportDate, sätter insjuknande till', startDate);
      }
    } else {
      // TEST MODE: reportDate saknas, simulera 5 dagar sedan
      const testDate = new Date();
      testDate.setDate(testDate.getDate() - 5);
      startDate = testDate.toISOString().split('T')[0];
      console.log('⚠️ TEST MODE: reportDate saknas, sätter insjuknande till', startDate);
    }
    
    console.log('📅 Calculated startDate:', startDate);
    
    const initialPoints = [
      {
        id: 'insjuknande_initial',
        type: 'insjuknande',
        date: startDate,
        symptoms: reportData.symptoms || [],
        severity: reportData.severity || 'medium',
        notes: 'Ursprunglig rapport'
      }
    ];
    
    setTimelinePoints(initialPoints);
    console.log('📅 Timeline initialized with insjuknande punkt:', startDate);
  };

  const handleTimelineChange = (newPoints) => {
    console.log('📅 Timeline updated:', newPoints);
    setTimelinePoints(newPoints);
  };

  const handleAddSymptomDirectly = () => {
    setShowSymptomPopup(true);
  };

  const handleAddSymptomPoint = (newPoint) => {
    const insjuknandePoint = timelinePoints.find(p => p.type === 'insjuknande');
    if (insjuknandePoint && new Date(newPoint.date) <= new Date(insjuknandePoint.date)) {
      setError('Nya symptom måste komma efter insjuknande-datumet');
      return;
    }
    
    const allPoints = [];
    timelinePoints.forEach(point => {
      allPoints.push({...point});
    });
    
    allPoints.push({
      ...newPoint,
      id: newPoint.id || `nya_symptom_${Date.now()}`
    });
    
    const sortedPoints = allPoints.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    setTimelinePoints(sortedPoints);
    setShowSymptomPopup(false);
    setError(null);
    
    console.log('✅ Timeline uppdaterad med', sortedPoints.length, 'punkter');
  };

  const addRecoveryPoint = () => {
    const latestDate = timelinePoints.reduce((latest, point) => {
      const pointDate = new Date(point.date);
      return pointDate > latest ? pointDate : latest;
    }, new Date(timelinePoints[0]?.date || new Date()));
    
    const nextDay = new Date(latestDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const recoveryDate = nextDay.toISOString().split('T')[0];
    
    const recoveryPoint = {
      id: 'recovery_' + Date.now(),
      type: 'frisk',
      date: recoveryDate,
      symptoms: [],
      severity: 'none',
      notes: 'Återhämtad'
    };
    
    const filteredPoints = timelinePoints.filter(point => point.type !== 'frisk');
    const updatedPoints = [...filteredPoints, recoveryPoint].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    setTimelinePoints(updatedPoints);
  };

  const getRecoveryDate = () => {
    const recoveryPoint = timelinePoints.find(point => point.type === 'frisk');
    return recoveryPoint ? recoveryPoint.date : null;
  };

  const calculateDaysSick = () => {
    const insjuknandePunkt = timelinePoints.find(point => point.type === 'insjuknande');
    const friskPunkt = timelinePoints.find(point => point.type === 'frisk');
    
    if (!insjuknandePunkt || !friskPunkt) return null;
    
    const startDate = new Date(insjuknandePunkt.date);
    const endDate = new Date(friskPunkt.date);
    const diffTime = endDate - startDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(1, diffDays);
  };

  // ✅ NEW: Calculate total unique symptoms across all timeline points
  const calculateTotalSymptoms = () => {
    const allSymptoms = new Set();
    timelinePoints.forEach(point => {
      if (point.symptoms && Array.isArray(point.symptoms)) {
        point.symptoms.forEach(symptom => allSymptoms.add(symptom));
      }
    });
    return allSymptoms.size;
  };

  const hasRecoveryPoint = () => {
    return timelinePoints.some(point => point.type === 'frisk');
  };

  const canAddSymptoms = () => {
    const hasInsjuknande = timelinePoints.some(p => p.type === 'insjuknande');
    const hasFrisk = timelinePoints.some(p => p.type === 'frisk');
    return hasInsjuknande && !hasFrisk;
  };

  const handleSubmitRecovery = async (e) => {
    e.preventDefault();
    
    const recoveryDate = getRecoveryDate();
    if (!recoveryDate) {
      setError('Markera när du blev frisk genom att lägga till en grön punkt på tidslinjen');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await fetch(`${API_CONFIG.baseUrl}/recovery/${code}/recovered`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recoveredNotes: recoveryNotes.trim(),
          recoveredDate: recoveryDate
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Recovery submitted successfully');
        setStep('success');
      } else {
        setError(result.error || 'Failed to submit recovery');
      }
      
    } catch (err) {
      console.error('❌ Error submitting recovery:', err);
      setError('Failed to submit recovery. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="recovery-container">
        <div className="recovery-card loading">
          <div className="loading-spinner"></div>
          <h2>Laddar din rapport...</h2>
          <p>Hämtar data för återhämtningskod: {code}</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="recovery-container">
        <div className="recovery-card error">
          <div className="error-icon">❌</div>
          <h2>Något gick fel</h2>
          <p className="error-message">{error}</p>
          <div className="recovery-actions">
            <button 
              className="btn-secondary"
              onClick={() => navigate('/reports')}
            >
              🔄 Gör ny rapport
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'already_recovered') {
    return (
      <div className="recovery-container">
        <div className="recovery-card already-recovered">
          <div className="success-icon">✅</div>
          <h2>Redan rapporterad!</h2>
          <p>Du har redan rapporterat återhämtning för denna rapport.</p>
          
          {reportData && (
            <div className="original-report">
              <h3>📋 Original rapport:</h3>
              <p><strong>Symptom:</strong> {reportData.symptoms?.join(', ')}</p>
              <p><strong>Område:</strong> {reportData.city}</p>
              <p><strong>Rapporterad:</strong> {new Date(reportData.reportDate).toLocaleDateString('sv-SE')}</p>
            </div>
          )}
          
          <div className="recovery-actions">
            <button
              className="btn-primary"
              onClick={() => navigate('/reports')}
            >
              🔄 Ny rapport
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    const daysSick = calculateDaysSick();
    const recoveryDate = getRecoveryDate();
    const totalSymptoms = calculateTotalSymptoms(); // ✅ FIXED: Count all unique symptoms
    
    return (
      <div className="recovery-container">
        <div className="recovery-card success">
          <div className="success-icon">🎉</div>
          <h2>Tack för ditt bidrag!</h2>
          <p>Din återhämtning har rapporterats och hjälper andra att få bättre prognoser.</p>
          
          <div className="recovery-summary">
            <h3>📊 Din återhämtning:</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-number">{daysSick || 0}</span>
                <span className="stat-label">dagar sjuk</span>
              </div>
              <div className="stat">
                <span className="stat-number">{totalSymptoms}</span>
                <span className="stat-label">symptom</span>
              </div>
            </div>
            {recoveryDate && (
              <p className="recovery-date">
                <strong>Frisk:</strong> {new Date(recoveryDate).toLocaleDateString('sv-SE')}
              </p>
            )}
          </div>

          <div className="community-impact">
            <h3>🌟 Ditt bidrag hjälper:</h3>
            <ul>
              <li>✅ Andra får bättre återhämtningsprognoser</li>
              <li>✅ Bygger kollektiv hälsointelligens</li>
              <li>✅ Förbättrar community insikter</li>
            </ul>
          </div>

          <div className="next-steps">
            <div className="recovery-actions">
              <button
                className="btn-primary"
                onClick={() => navigate('/reports')}
              >
                🔄 Ny symptomrapport
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recovery-container">
      <div className="recovery-card">
        <div className="recovery-header">
          <h1>🌟 Rapportera återhämtning</h1>
          {reportData && (
            <p className="report-meta">
              <strong>Rapporterad:</strong> {reportData.reportDate ? (() => {
                const date = new Date(reportData.reportDate);
                return !isNaN(date.getTime()) 
                  ? date.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })
                  : new Date().toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
              })() : new Date().toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })} • 
              <strong> Symptom:</strong> {reportData.symptoms?.join(', ')} • 
              <strong> Område:</strong> {reportData.postalCode} {reportData.city || 'Ej angivet'} •
              <strong> Kod:</strong> {code}
            </p>
          )}
        </div>

        <div className="timeline-section">
          {timelinePoints.length > 0 && (
            <RecoveryTimelineSlider
              timelinePoints={timelinePoints}
              onPointsChange={handleTimelineChange}
              startDate={reportData?.reportDate}
            />
          )}
          
          {/* ✅ FIXED: Timeline Footer Box - knappar först, legend sen */}
          <div className="timeline-footer">
            {/* Action buttons FÖRST i HTML */}
            <div className="timeline-actions">
              {canAddSymptoms() && (
                <button
                  type="button"
                  className="btn-recovery btn-symptom"
                  onClick={() => handleAddSymptomDirectly()}
                >
                  🟡 Lägg till nya symptom
                </button>
              )}
              
              <button
                type="button"
                className="btn-recovery btn-healthy"
                onClick={addRecoveryPoint}
                disabled={hasRecoveryPoint()}
              >
                {hasRecoveryPoint() ? '✅ Frisk-punkt tillagd' : '🟢 Markera frisk'}
              </button>
            </div>

            {/* Legend SEN i HTML */}
            <div className="timeline-header">
              <p>🔴 = Insjuknande • 🟡 = Nya symptom • 🟢 = Frisk</p>
            </div>
          </div>

          {hasRecoveryPoint() && (
            <div className="duration-display">
              <strong>⏱️ Sjukdomslängd: {calculateDaysSick()} dagar</strong>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmitRecovery} className="recovery-form">
          <div className="form-group">
            <label htmlFor="recoveryNotes" className="form-label">
              📝 Ytterligare kommentarer (valfritt)
            </label>
            <textarea
              id="recoveryNotes"
              className="form-input"
              rows="3"
              value={recoveryNotes}
              onChange={(e) => setRecoveryNotes(e.target.value)}
              placeholder="T.ex. vad som hjälpte, hur du mådde under återhämtningen..."
            />
          </div>

          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !hasRecoveryPoint()}
            >
              {submitting ? (
                <>
                  <span className="loading-spinner"></span>
                  Rapporterar...
                </>
              ) : (
                <>
                  📈 Rapportera återhämtning
                </>
              )}
            </button>
          </div>
        </form>

        <div className="why-important">
          <h3>💡 Varför är detta viktigt?</h3>
          <ul>
            <li>Hjälper andra få bättre prognoser för samma symptom</li>
            <li>Bygger kollektiv hälsointelligens för ditt område</li>
            <li>Tar bara 30 sekunder att rapportera</li>
            <li>Helt anonymt och säkert</li>
          </ul>
        </div>

        <SymptomPopup
          isOpen={showSymptomPopup}
          existingPoints={timelinePoints}
          onConfirm={handleAddSymptomPoint}
          onCancel={() => setShowSymptomPopup(false)}
        />
      </div>
    </div>
  );
};

export default Recovery;